// pattern: Imperative Shell

import { type Signal, signal, batch } from '@preact/signals'
import Route from 'route-event'
import {
    createDocument,
    applyRouteDraft,
    getRouteDraft,
    serializeDocument,
    SUPPORTED_METHODS,
    updateRouteDraft,
    removeRoute,
    normalizePath,
    parsePathParameters,
    emptyMethodDraft,
    emptyResponseEntry,
    CURATED_STATUS_CODES,
    emptySchemaNode,
    setNodeAtPath,
    emptyProperty,
    applyNodeTypeChange,
    applyNodeFileToggle,
    type CanonicalApiDocument,
    type ParameterEntry,
    type MethodDraft,
    type ResponseEntry,
    type SelectedRouteIdentity,
    type HttpMethod,
    type ApplyRouteDraftResult,
    type SchemaNode,
    type SchemaPath,
    type SchemaNodeType,
    type StringFormat,
    type XmlModifier,
} from './lib/openapi-document.js'
// import Debug from '@substrate-system/debug'
// const debug = Debug(import.meta.env.DEV)

/**
 * The in-memory, editable working copy of a single API route that the
 * route-editing form (`src/routes/home.ts`) binds to via the
 * `state.routeDraft` signal. It is the flat, UI-friendly mirror of the
 * canonical document's route shape: the route-data fields below match
 * `RouteDraftInput` (what the pure `applyRouteDraft` / `updateRouteDraft`
 * reconcilers in `openapi-document.ts` consume), plus editor-only UI
 * state. Construct one with `emptyDraft()`; load an existing path into one
 * with `State.SelectRoute`.
 */
export interface RouteDraft {
    // --- route data (handed to the reconciler on submit) ---
    // The path template being edited, e.g. `/users/{id}`.
    pathInput:string;
    // HTTP methods toggled on for this path, kept in canonical order.
    selectedMethods:string[];
    // Shared path-level params (all in:'path'); synced from `pathInput`
    // by `syncPathParameters` as the user types.
    pathParameters:ParameterEntry[];
    // Per-method drafts keyed by HTTP method; each carries its own
    // summary, query params, request body, and responses.
    operations:Record<string, MethodDraft>;

    // --- editor UI state ---
    // Whether we are creating a new route or editing an existing one.
    mode:'add'|'edit';
    // Transient inline feedback, all cleared on every mutation.
    error:string;          // validation / submit failure message
    successMessage:string; // confirmation after a successful add/update
    copiedMessage:string;  // confirmation after copying JSON to clipboard
}

export type SchemaTarget =
    | { kind:'requestBody'; method:string }
    | { kind:'response'; method:string; index:number }

function emptyDraft ():RouteDraft {
    return {
        pathInput: '',
        selectedMethods: [],
        pathParameters: [],
        operations: {},
        mode: 'add',
        error: '',
        successMessage: '',
        copiedMessage: '',
    }
}

/**
 * Setup any state
 *   - routes
 */
export function State ():{
    route:Signal<string>;
    count:Signal<number>;
    document:Signal<CanonicalApiDocument>;
    routeDraft:Signal<RouteDraft>;
    selectedRoute:Signal<SelectedRouteIdentity|null>;
    routeEditSession:Signal<number>;
    documentText:Signal<string>;
    editorOpen:Signal<boolean>;
    copyVersion:Signal<number>;
    pendingRemovals:Signal<string[]>;
    _setRoute:(path:string)=>void;
} {  // eslint-disable-line indent
    const onRoute = Route()

    const state = {
        _setRoute: onRoute.setRoute.bind(onRoute),
        count: signal<number>(0),
        document: signal<CanonicalApiDocument>(createDocument()),
        routeDraft: signal<RouteDraft>(emptyDraft()),
        selectedRoute: signal<SelectedRouteIdentity|null>(null),
        // Identity of the current editing session. The view keys every
        // disclosure's remount on this token, so it must change ONLY when a
        // genuinely new editing context begins (load a different route, start
        // a new route, or clear the editor after removing the selected route).
        // A save deliberately does NOT advance it -- see State.SubmitRoute.
        routeEditSession: signal<number>(0),
        editorOpen: signal<boolean>(false),
        documentText: signal<string>(''),
        copyVersion: signal<number>(0),
        pendingRemovals: signal<string[]>([]),
        route: signal<string>(location.pathname + location.search)
    }

    state.documentText.value = serializeDocument(state.document.value)

    /**
     * set the app state to match the browser URL
     */
    onRoute((path:string, data) => {
        // for github pages
        const newPath = path.replace('/template-ts-preact-htm/', '/')
        state.route.value = newPath
        // handle scroll state like a web browser
        // (restore scroll position on back/forward)
        if (data.popstate) {
            return window.scrollTo(data.scrollX, data.scrollY)
        }
        // if this was a link click (not back button), then scroll to top
        window.scrollTo(0, 0)
    })

    return state
}

State.Increase = function (state:ReturnType<typeof State>) {
    state.count.value++
}

State.Decrease = function (state:ReturnType<typeof State>) {
    state.count.value--
}

function syncPathParameters (
    pathInput:string,
    current:ParameterEntry[]
):ParameterEntry[] {
    const tokens = parsePathParameters(pathInput)
    return tokens.map(token => {
        const existing = current.find(p => p.name === token.name)
        if (existing) return existing
        return {
            name: token.name,
            in: 'path' as const,
            type: 'string' as const,
            required: true as const,
            description: '',
        }
    })
}

State.UpdatePath = function (
    state:ReturnType<typeof State>,
    value:string
) {
    const draft = state.routeDraft.value
    state.routeDraft.value = {
        ...draft,
        pathInput: value,
        pathParameters: syncPathParameters(
            value,
            draft.pathParameters
        ),
        error: '',
        successMessage: '',
        copiedMessage: '',
    }
}

State.UpdatePathParameterField = function (
    state:ReturnType<typeof State>,
    index:number,
    field:keyof ParameterEntry,
    value:string|boolean
) {
    const pathParameters = state.routeDraft.value.pathParameters.map(
        (param, idx) => {
            if (idx !== index) return param
            if (field === 'required' && typeof value === 'boolean') {
                return { ...param, required: value }
            }
            return { ...param, [field]: value as string }
        }
    )
    state.routeDraft.value = {
        ...state.routeDraft.value,
        pathParameters,
        error: '',
        successMessage: '',
        copiedMessage: '',
    }
}

function inCanonicalOrder (methods:string[]):string[] {
    return SUPPORTED_METHODS.filter(m => methods.includes(m))
}

State.ToggleMethod = function (
    state:ReturnType<typeof State>,
    method:string
) {
    if (!SUPPORTED_METHODS.includes(
        method as typeof SUPPORTED_METHODS[number]
    )) {
        return
    }

    const draft = state.routeDraft.value
    const isSelected = draft.selectedMethods.includes(method)
    const operations = { ...draft.operations }
    let selectedMethods:string[]

    if (isSelected) {
        selectedMethods = inCanonicalOrder(
            draft.selectedMethods.filter(m => m !== method)
        )
        delete operations[method]
    } else {
        selectedMethods = inCanonicalOrder(
            [...draft.selectedMethods, method]
        )
        operations[method] = draft.operations[method] || emptyMethodDraft()
    }

    state.routeDraft.value = {
        ...draft,
        selectedMethods,
        operations,
        error: '',
        successMessage: '',
        copiedMessage: '',
    }
}

function updateOperation (
    draft:RouteDraft,
    method:string,
    change:(md:MethodDraft) => MethodDraft
):RouteDraft {
    const current = draft.operations[method]
    if (!current) return draft
    return {
        ...draft,
        operations: { ...draft.operations, [method]: change(current) },
        error: '',
        successMessage: '',
        copiedMessage: '',
    }
}

function updateSchemaModel (
    draft:RouteDraft,
    target:SchemaTarget,
    path:SchemaPath,
    change:(node:SchemaNode) => SchemaNode
):RouteDraft {
    return updateOperation(draft, target.method, md => {
        if (target.kind === 'requestBody') {
            const model = md.requestBodySchemaModel ?? emptySchemaNode()
            return {
                ...md,
                requestBodySchemaModel: setNodeAtPath(model, path, change),
            }
        }
        return {
            ...md,
            responses: md.responses.map((resp, i) => {
                if (i !== target.index) return resp
                const model = resp.schemaModel ?? emptySchemaNode()
                return {
                    ...resp,
                    schemaModel: setNodeAtPath(model, path, change),
                }
            }),
        }
    })
}

State.UpdateMethodSummary = function (
    state:ReturnType<typeof State>,
    method:string,
    summary:string
) {
    state.routeDraft.value = updateOperation(
        state.routeDraft.value,
        method,
        md => ({ ...md, summary })
    )
}

State.AddQueryParameter = function (
    state:ReturnType<typeof State>,
    method:string
) {
    state.routeDraft.value = updateOperation(
        state.routeDraft.value,
        method,
        md => ({
            ...md,
            queryParameters: [
                ...md.queryParameters,
                {
                    name: '',
                    in: 'query',
                    type: 'string',
                    required: false,
                    description: '',
                },
            ],
        })
    )
}

State.UpdateQueryParameterField = function (
    state:ReturnType<typeof State>,
    method:string,
    index:number,
    field:keyof ParameterEntry,
    value:string|boolean
) {
    state.routeDraft.value = updateOperation(
        state.routeDraft.value,
        method,
        md => ({
            ...md,
            queryParameters: md.queryParameters.map((param, idx) => {
                if (idx !== index) return param
                if (field === 'required' && typeof value === 'boolean') {
                    return { ...param, required: value }
                }
                return { ...param, [field]: value as string }
            }),
        })
    )
}

State.RemoveQueryParameter = function (
    state:ReturnType<typeof State>,
    method:string,
    index:number
) {
    state.routeDraft.value = updateOperation(
        state.routeDraft.value,
        method,
        md => ({
            ...md,
            queryParameters: md.queryParameters.filter((_, idx) =>
                idx !== index
            ),
        })
    )
}

State.AddResponse = function (
    state:ReturnType<typeof State>,
    method:string
) {
    state.routeDraft.value = updateOperation(
        state.routeDraft.value,
        method,
        md => {
            const used = new Set(md.responses.map(r => r.status))
            const next = CURATED_STATUS_CODES.find(
                code => !used.has(code)
            ) ?? ''
            const entry:ResponseEntry = {
                ...emptyResponseEntry(),
                status: next,
                description: '',
                expanded: true,
            }
            return { ...md, responses: [...md.responses, entry] }
        }
    )
}

State.UpdateResponseField = function (
    state:ReturnType<typeof State>,
    method:string,
    index:number,
    field:'status'|'description'|'schema'|'example'|'mediaType',
    value:string
) {
    state.routeDraft.value = updateOperation(
        state.routeDraft.value,
        method,
        md => ({
            ...md,
            responses: md.responses.map((resp, idx) =>
                idx === index ? { ...resp, [field]: value } : resp
            ),
        })
    )
}

State.RemoveResponse = function (
    state:ReturnType<typeof State>,
    method:string,
    index:number
) {
    state.routeDraft.value = updateOperation(
        state.routeDraft.value,
        method,
        md => ({
            ...md,
            responses: md.responses.filter((_, idx) => idx !== index),
        })
    )
}

State.AddProperty = function (
    state:ReturnType<typeof State>,
    target:SchemaTarget,
    path:SchemaPath
) {
    state.routeDraft.value = updateSchemaModel(
        state.routeDraft.value, target, path,
        node => ({
            ...node,
            properties: [...(node.properties || []), emptyProperty()],
        })
    )
}

State.RemoveProperty = function (
    state:ReturnType<typeof State>,
    target:SchemaTarget,
    path:SchemaPath,
    index:number
) {
    state.routeDraft.value = updateSchemaModel(
        state.routeDraft.value, target, path,
        node => ({
            ...node,
            properties: (node.properties || []).filter(
                (_, i) => i !== index
            ),
        })
    )
}

State.UpdatePropertyName = function (
    state:ReturnType<typeof State>,
    target:SchemaTarget,
    path:SchemaPath,
    index:number,
    name:string
) {
    state.routeDraft.value = updateSchemaModel(
        state.routeDraft.value, target, path,
        node => ({
            ...node,
            properties: (node.properties || []).map((p, i) =>
                i === index ? { ...p, name } : p
            ),
        })
    )
}

State.UpdatePropertyRequired = function (
    state:ReturnType<typeof State>,
    target:SchemaTarget,
    path:SchemaPath,
    index:number,
    required:boolean
) {
    state.routeDraft.value = updateSchemaModel(
        state.routeDraft.value, target, path,
        node => ({
            ...node,
            properties: (node.properties || []).map((p, i) =>
                i === index ? { ...p, required } : p
            ),
        })
    )
}

State.UpdateRequestBodyField = function (
    state:ReturnType<typeof State>,
    method:string,
    field:'mediaType'|'schema'|'example',
    value:string
) {
    state.routeDraft.value = updateOperation(
        state.routeDraft.value,
        method,
        md => ({
            ...md,
            ...(field === 'mediaType' ?
                { requestBodyMediaType: value } :
                field === 'schema' ?
                    { requestBodySchema: value } :
                    { requestBodyExample: value }),
        })
    )
}

State.UpdatePropertyType = function (
    state:ReturnType<typeof State>,
    target:SchemaTarget,
    path:SchemaPath,
    type:SchemaNodeType
) {
    state.routeDraft.value = updateSchemaModel(
        state.routeDraft.value, target, path,
        node => applyNodeTypeChange(node, type)
    )
}

State.UpdatePropertyFormat = function (
    state:ReturnType<typeof State>,
    target:SchemaTarget,
    path:SchemaPath,
    format:StringFormat | ''
) {
    state.routeDraft.value = updateSchemaModel(
        state.routeDraft.value, target, path,
        node => {
            if (node.type !== 'string') return node
            const next:SchemaNode = { ...node }
            if (format === '') delete next.format
            else next.format = format
            return next
        }
    )
}

State.SetPropertyFile = function (
    state:ReturnType<typeof State>,
    target:SchemaTarget,
    path:SchemaPath,
    file:boolean
) {
    state.routeDraft.value = updateSchemaModel(
        state.routeDraft.value, target, path,
        node => applyNodeFileToggle(node, file)
    )
}

State.UpdatePropertyContentMediaType = function (
    state:ReturnType<typeof State>,
    target:SchemaTarget,
    path:SchemaPath,
    value:string
) {
    state.routeDraft.value = updateSchemaModel(
        state.routeDraft.value, target, path,
        node => ({ ...node, contentMediaType: value })
    )
}

State.UpdateNodeXml = function (
    state:ReturnType<typeof State>,
    target:SchemaTarget,
    path:SchemaPath,
    field:'name' | 'namespace' | 'prefix' | 'attribute' | 'wrapped',
    value:string | boolean
) {
    state.routeDraft.value = updateSchemaModel(
        state.routeDraft.value, target, path,
        node => {
            const xml:XmlModifier = { ...(node.xml || {}) }
            if (field === 'attribute' || field === 'wrapped') {
                xml[field] = value as boolean
            } else {
                xml[field] = value as string
            }
            return { ...node, xml }
        }
    )
}

State.SubmitRoute = function (state:ReturnType<typeof State>) {
    const draft = state.routeDraft.value
    const input = {
        pathInput: draft.pathInput,
        selectedMethods: draft.selectedMethods,
        pathParameters: draft.pathParameters,
        operations: draft.operations,
    }

    let result:ApplyRouteDraftResult
    if (draft.mode === 'edit' && state.selectedRoute.value) {
        result = updateRouteDraft(
            state.document.value,
            input,
            state.selectedRoute.value
        )
    } else {
        result = applyRouteDraft(state.document.value, input)
    }

    if (!result.ok) {
        state.routeDraft.value = {
            ...draft,
            error: result.error,
            successMessage: '',
            copiedMessage: '',
        }
        return
    }

    const wasEdit = draft.mode === 'edit' && !!state.selectedRoute.value
    const methodList = result.methods.map(method => method.toUpperCase()).join(', ')

    batch(() => {
        state.document.value = result.document
        state.documentText.value = serializeDocument(result.document)
        // Keep the form in place (only swap the inline feedback). Resetting the
        // draft would collapse the form; on a draft taller than the viewport
        // that shrinks the page below the current scroll offset, forcing the
        // browser to clamp the scroll down onto the document output. Keeping
        // the form in place means the page never shrinks and the viewport never
        // moves. A fresh draft is started explicitly via the "+" (new route)
        // control.
        //
        // routeEditSession is DELIBERATELY left untouched: a save is the same
        // editing session, so the view must NOT remount (and therefore
        // collapse) the disclosures. Re-pointing selectedRoute below would, on
        // its own, have changed the view's remount key and collapsed the form
        // -- which is exactly the scroll-on-save bug this avoids.
        //
        // The saved route now lives in the document, so point the selection at
        // it and switch the draft to edit mode. Without this, a second save of
        // the same form would re-run the ADD path against a document that
        // already contains this route and fail with a spurious "already has
        // <METHOD>" error; tracking result.path/result.methods routes the next
        // save through updateRouteDraft, which updates in place. This also
        // keeps the selection correct after a rename or method change.
        state.selectedRoute.value = {
            originalPath: result.path,
            originalMethods: [...result.methods],
        }
        state.routeDraft.value = {
            ...draft,
            mode: 'edit',
            error: '',
            successMessage: `${wasEdit ? 'Updated' : 'Added'} ` +
                `${result.path} (${methodList})`,
            copiedMessage: '',
        }
    })
}

let copySuccessTimer:ReturnType<typeof setTimeout>|null = null

State.CopyDocument = async function (state:ReturnType<typeof State>) {
    if (!navigator.clipboard) {
        state.routeDraft.value = {
            ...state.routeDraft.value,
            copiedMessage: 'Clipboard is not available in this browser.',
        }
        return
    }

    await navigator.clipboard.writeText(state.documentText.value)

    const nextVersion = state.copyVersion.value + 1

    batch(() => {
        state.routeDraft.value = {
            ...state.routeDraft.value,
            copiedMessage: 'Copied JSON to the clipboard.',
        }
        state.copyVersion.value = nextVersion
    })

    if (copySuccessTimer !== null) {
        clearTimeout(copySuccessTimer)
    }
    copySuccessTimer = setTimeout(() => {
        if (state.copyVersion.value === nextVersion) {
            state.copyVersion.value = 0
        }
        copySuccessTimer = null
    }, 4000)
}

State.OpenEditor = function (state:ReturnType<typeof State>) {
    batch(() => {
        state.editorOpen.value = true
        state.selectedRoute.value = null
        state.routeDraft.value = emptyDraft()
    })
}

State.CloseEditor = function (state:ReturnType<typeof State>) {
    batch(() => {
        state.editorOpen.value = false
        state.selectedRoute.value = null
        state.routeDraft.value = emptyDraft()
    })
}

State.SelectRoute = function (
    state:ReturnType<typeof State>,
    path:string
) {
    const loaded = getRouteDraft(state.document.value, path)
    if (!loaded) return

    const pathParameters = syncPathParameters(
        loaded.pathInput, loaded.pathParameters
    )

    batch(() => {
        state.editorOpen.value = true
        // Loading a different route is a new editing context: advance the
        // session token so the view remounts the disclosures fresh.
        state.routeEditSession.value++
        state.selectedRoute.value = {
            originalPath: loaded.pathInput,
            originalMethods: [...loaded.selectedMethods] as HttpMethod[],
        }
        state.routeDraft.value = {
            pathInput: loaded.pathInput,
            selectedMethods: loaded.selectedMethods,
            pathParameters,
            operations: loaded.operations,
            mode: 'edit',
            error: '',
            successMessage: '',
            copiedMessage: '',
        }
    })
}

State.UpdateTitle = function (
    state:ReturnType<typeof State>,
    title:string
) {
    const updatedDoc = {
        ...state.document.value,
        info: {
            ...state.document.value.info,
            title,
        },
    }
    batch(() => {
        state.document.value = updatedDoc
        state.documentText.value = serializeDocument(updatedDoc)
    })
}

State.ClearSelectedRoute = function (state:ReturnType<typeof State>) {
    batch(() => {
        // Discarding the selection + draft starts a fresh editing context
        // (also the StartNewRoute path): advance the session token.
        state.routeEditSession.value++
        state.selectedRoute.value = null
        state.routeDraft.value = emptyDraft()
    })
}

// Start authoring a brand-new route: discard any selection and any unsaved
// draft edits, landing on a fresh empty draft. Pure data only — the view
// (src/routes/home.ts) owns the post-render focus move to #route-path.
// Delegates to ClearSelectedRoute so there is one canonical reset path; the
// document/documentText/pendingRemovals signals are intentionally untouched.
State.StartNewRoute = function (state:ReturnType<typeof State>) {
    State.ClearSelectedRoute(state)
}

// Open an inline removal confirmation for `path`. Pure data only — the view
// (src/routes/home.ts) owns the DOM/focus side effects. No-op if the path
// already has an open confirmation.
State.BeginRemoveRoute = function (
    state:ReturnType<typeof State>,
    path:string
) {
    const current = state.pendingRemovals.value
    if (current.includes(path)) return
    state.pendingRemovals.value = [...current, path]
}

// Dismiss the inline removal confirmation for `path` without deleting the
// route. Touches only pendingRemovals (document/selection/draft untouched).
State.CancelRemoveRoute = function (
    state:ReturnType<typeof State>,
    path:string
) {
    const current = state.pendingRemovals.value
    if (!current.includes(path)) return
    state.pendingRemovals.value = current.filter(p => p !== path)
}

// Confirm removal: delete `path` from the document, re-derive documentText,
// and prune pendingRemovals to the confirmed path removed plus any stale
// paths that no longer exist. The selection-reset branch is added in US3.
State.ConfirmRemoveRoute = function (
    state:ReturnType<typeof State>,
    path:string
) {
    const next = removeRoute(state.document.value, path)
    const remaining = state.pendingRemovals.value
        .filter(p => p !== path)
        .filter(p => next.paths[p])
    const removesSelected = state.selectedRoute.value?.originalPath ===
        normalizePath(path)

    batch(() => {
        state.document.value = next
        state.documentText.value = serializeDocument(next)
        state.pendingRemovals.value = remaining
        // FR-008: removing the route loaded in the editor resets the editor;
        // removing any other route leaves the current selection/draft alone.
        if (removesSelected) {
            // Resetting the editor is a new editing context: advance the token.
            state.routeEditSession.value++
            state.selectedRoute.value = null
            state.routeDraft.value = emptyDraft()
        }
    })
}
